import { FC } from 'react';
import { format } from 'date-fns';
import { useTranslate } from 'hooks/useTranslate';

import { dateFormat } from 'constants/formats';
import { Partner, TradingOffer, TradingOfferOperation } from 'types';

import { formatCurrencyDecimals, formatNumber } from 'utils/formats';
import { getInitials } from 'utils/getInitials';

import ToggleMenu from 'modules/_shared/components/ToggleMenu';
import TableRow from 'modules/_shared/Table/components/TableRow';
import { RowCell } from 'modules/_shared/Table/types/RowCell';
import getOfferReference from 'modules/trading/utils/getOfferReference';
import getOperationReference from 'modules/trading/utils/getOperationReference';
import { statusBadgeMap } from 'modules/trading/constants/tradingOfferOperationStatus';
import { isTerminalStatus } from 'modules/trading/modals/EditTradingOfferOperationStatus/utils/isTerminalStatus';

import { getCounterparty } from '../utils/getCounterparty';
import { isPendingForPartner } from '../utils/isPendingOperationForPartner';
import { useTradingOfferOperations } from '../context/TradingOfferOperationsContext';
import { getOperationType } from '../utils/getOperationType';

type TradingOfferOperationsTableRowProps = {
  index: number;
  operation: TradingOfferOperation;
};

const TradingOfferOperationsTableRow: FC<
  TradingOfferOperationsTableRowProps
> = ({ index, operation }) => {
  const { t } = useTranslate();

  const { data, actions } = useTradingOfferOperations();

  const getTypeBadge = (type: 'BUY' | 'SELL') => {
    if (type === 'BUY') {
      return (
        <div className="d-flex align-items-center">
          <div className="icon-circle bg-primary-dim text-primary mr-2">
            <em className="icon ni ni-arrow-down-circle" />
          </div>
          <span className="badge badge-dim badge-primary">{t('Purchase')}</span>
        </div>
      );
    }
    return (
      <div className="d-flex align-items-center">
        <div className="icon-circle bg-success-dim text-success mr-2">
          <em className="icon ni ni-arrow-up-circle" />
        </div>
        <span className="badge badge-dim bg-success">{t('Sale')}</span>
      </div>
    );
  };

  const badge = statusBadgeMap[operation.status] || statusBadgeMap.PENDING;
  const opType =
    data.isPartnerView && data.partnerId
      ? getOperationType(operation, data.partnerId)
      : 'BUY';
  const pendingForPartner =
    data.isPartnerView &&
    !!data.partnerId &&
    isPendingForPartner(operation, data.partnerId);

  const buyer = operation.buyer as Partner;
  const seller = operation.seller as Partner;

  const isTerminal = isTerminalStatus(operation.status);

  const handleView = () => actions.handleViewTradingOfferOperation(index);
  const handleEdit = () => actions.handleEditTradingOfferOperationStatus(index);

  const toggleOptions = [
    <a
      className="d-flex cursor-pointer"
      key={`holding-class-view-option-${index}`}
      onClick={handleView}
    >
      <em className="icon ni ni-eye fs-20px" />
      <span className="ml-1">{t('See')}</span>
    </a>,
    !isTerminal && data.isAdmin && (
      <a
        className="d-flex cursor-pointer"
        key={`holding-class-edit-option-${index}`}
        onClick={handleEdit}
      >
        <em className="icon ni ni-edit fs-20px" />
        <span className="ml-1">{t('ChangeStatus')}</span>
      </a>
    ),
  ];

  const hasOptions = !data.isReadOnly;

  const cells = [
    data.isPartnerView && {
      key: `operation-${index}`,
      content: getTypeBadge(opType),
      onClick: handleView,
    },

    data.isPartnerView && {
      key: `offerRef-${index}`,
      content: (
        <span className="ref text-base badge badge-light">
          {getOfferReference(operation.tradingOffer as TradingOffer)}
        </span>
      ),
      onClick: handleView,
    },

    {
      key: `ref-${index}`,
      content: (
        <span className="ref text-base badge badge-light">
          {getOperationReference(operation)}
        </span>
      ),
      onClick: handleView,
    },

    {
      key: `date-${index}`,
      content: (
        <span className="ref text-base badge badge-light">
          {format(new Date(operation.requestDate), dateFormat)}
        </span>
      ),
      onClick: handleView,
    },

    data.isPartnerView && {
      key: `counterparty-${index}`,
      content: data.partnerId
        ? getCounterparty(operation, data.partnerId, t)
        : '-',
      onClick: handleView,
    },

    !data.isPartnerView && {
      key: `buyer-${index}`,
      content: (
        <span className="d-inline-flex align-items-center">
          <div className="user-avatar user-avatar-xs bg-info-dim mr-1 w-25px h-25px fs-12px">
            {buyer?.image ? (
              <img src={buyer.image} alt={buyer.name} />
            ) : (
              <span>{getInitials(buyer?.name)}</span>
            )}
          </div>
          {buyer?.name || '-'}
        </span>
      ),
      onClick: handleView,
    },

    !data.isPartnerView && {
      key: `seller-${index}`,
      content: (
        <span className="d-inline-flex align-items-center">
          <div className="user-avatar user-avatar-xs bg-info-dim mr-1 w-25px h-25px fs-12px">
            {seller?.image ? (
              <img src={seller.image} alt={seller.name} />
            ) : (
              <span>{getInitials(seller?.name)}</span>
            )}
          </div>
          {seller?.name || '-'}
        </span>
      ),
    },

    {
      key: `shares-${index}`,
      content: formatNumber(operation.sharesCount),
      onClick: handleView,
      textPosition: 'right',
    },

    {
      key: `amount-${index}`,
      content: formatCurrencyDecimals(operation.totalPrice),
      onClick: handleView,
      textPosition: 'right',
    },

    {
      key: `status-${index}`,
      content: (
        <>
          <span className={`badge badge-dim ${badge.className}`}>
            <em className={`icon ni ${badge.icon} mr-1`} />
            {t(badge.label)}
          </span>
          {pendingForPartner && (
            <span className="badge badge-warning ml-1 mt-1 d-inline-block">
              {t('PendingForYou')}
            </span>
          )}
        </>
      ),
      onClick: handleView,
    },

    hasOptions
      ? {
          key: `holding-class-options-${index}`,
          content: <ToggleMenu toggleOptions={toggleOptions} />,
          textPosition: 'center',
        }
      : null,
  ].filter(Boolean) as RowCell[];

  return <TableRow cells={cells} />;
};

export default TradingOfferOperationsTableRow;
